import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { MapContainer, Marker, Popup, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiRequest } from '../../utils/apiConfig';
import './MapPage.scss';

// Fix for default leaflet marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom CRS for Minecraft coordinates
const MinecraftCRS = L.extend({}, L.CRS.Simple, {
    projection: L.Projection.LonLat,
    scale: function (zoom) {
        return Math.pow(2, zoom);
    },
    zoom: function (scale) {
        return Math.log(scale) / Math.LN2;
    },
    distance: function (latlng1, latlng2) {
        var dx = latlng2.lng - latlng1.lng,
            dy = latlng2.lat - latlng1.lat;
        return Math.sqrt(dx * dx + dy * dy);
    },
    transformation: new L.Transformation(1, 0, -1, 0),
    infinite: true
});

// Helper functions for coordinate conversion
const blockToLatLng = (x, z, zoom = 0) => {
    // Convert from Minecraft world coordinates to Leaflet coordinates
    // At zoom 0, 1 leaflet unit = 16 blocks, so divide by 16
    return L.latLng(-z / 16, x / 16);
};

const mapBoundsToBlockBbox = (bounds, zoom) => {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    // Convert from Leaflet coordinates to Minecraft world coordinates
    // At zoom 0, 1 leaflet unit = 16 blocks, so multiply by 16
    return {
        minX: Math.floor(sw.lng * 16),
        minZ: Math.floor(-ne.lat * 16),
        maxX: Math.ceil(ne.lng * 16),
        maxZ: Math.ceil(-sw.lat * 16)
    };
};

// Custom tile layer for Minecraft biomes
const MinecraftTileLayer = ({ seed, version, dimension }) => {
    const map = useMap();

    useEffect(() => {
        if (!seed || !version || !dimension) return;

        const tileLayer = L.tileLayer(
            `/api/map/tiles/{z}/{x}/{y}.png?seed=${seed}&ver=${version}&dimension=${dimension}`,
            {
                minZoom: -3,
                maxZoom: 5,
                tileSize: 256,
                bounds: [[-8000, -8000], [8000, 8000]],
                noWrap: true,
            }
        );

        map.addLayer(tileLayer);

        return () => {
            map.removeLayer(tileLayer);
        };
    }, [map, seed, version, dimension]);

    return null;
};

// POI markers component
const PoiMarkers = ({ pois, toggledTypes }) => {
    const map = useMap();

    const getPoiIcon = (type) => {
        const colors = {
            village: '#8B4513',
            monument: '#4682B4',
            mansion: '#800080',
            outpost: '#DC143C',
            temple: '#FFD700',
        };

        return L.divIcon({
            className: 'poi-marker',
            html: `<div style="background-color: ${colors[type] || '#666'}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white;"></div>`,
            iconSize: [16, 16],
            iconAnchor: [8, 8],
        });
    };

    return (
        <>
            {pois
                .filter(poi => toggledTypes[poi.type])
                .map((poi, index) => (
                    <Marker
                        key={`${poi.type}-${poi.x}-${poi.z}-${index}`}
                        position={blockToLatLng(poi.x, poi.z, map.getZoom())}
                        icon={getPoiIcon(poi.type)}
                    >
                        <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                            <div>
                                <strong>{poi.type.charAt(0).toUpperCase() + poi.type.slice(1)}</strong>
                                <br />
                                X: {poi.x}, Z: {poi.z}
                            </div>
                        </Tooltip>
                    </Marker>
                ))}
        </>
    );
};

// Origin marker component for x:0, z:0
const OriginMarker = () => {
    const map = useMap();

    const getCompassIcon = () => {
        return L.divIcon({
            className: 'origin-marker',
            html: `
                <div style="
                    width: 20px; 
                    height: 20px; 
                    background-color: #ff4444;
                    border: 2px solid white;
                    border-radius: 50%;
                    position: relative;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                ">
                    <div style="
                        position: absolute;
                        top: 50%;
                        left: -7px;
                        right: 2px;
                        height: 2px;
                        background-color: white;
                        transform: translateY(-50%);
                        width: 34px;
                    "></div>
                    <div style="
                        position: absolute;
                        left: 50%;
                        top: -7px;
                        bottom: 2px;
                        width: 2px;
                        background-color: white;
                        transform: translateX(-50%);
                        height: 34px;
                    "></div>
                </div>
            `,
            iconSize: [28, 28],
            iconAnchor: [14, 14],
        });
    };

    return (
        <Marker
            position={blockToLatLng(0, 0, map.getZoom())}
            icon={getCompassIcon()}
        >
            <Tooltip direction="top" offset={[0, -15]} opacity={1}>
                <div>
                    <strong>Origin Point</strong>
                    <br />
                    X: 0, Z: 0
                </div>
            </Tooltip>
        </Marker>
    );
};

// Player markers component
const PlayerMarkers = ({ players, stale, highlightedPlayer, selectedDimension }) => {
    const map = useMap();
    const navigate = useNavigate();

    const getPlayerIcon = (playerName, stale, isHighlighted) => {
        const borderColor = stale ? '#ff6b6b' : '#51cf66';
        const glowEffect = isHighlighted ? 'box-shadow: 0 0 15px rgba(81, 207, 102, 0.8), 0 0 30px rgba(81, 207, 102, 0.4);' : 'box-shadow: 0 0 4px rgba(0,0,0,0.5);';

        return L.divIcon({
            className: 'player-marker',
            html: `<div style="width: 32px; height: 32px; border-radius: 4px; border: 3px solid ${borderColor}; ${glowEffect} overflow: hidden; background: white; cursor: pointer;">
                <img src="https://mc-heads.net/avatar/${playerName}" style="width: 100%; height: 100%; image-rendering: pixelated; image-rendering: -moz-crisp-edges; image-rendering: crisp-edges; display: block;" />
            </div>`,
            iconSize: [38, 38],
            iconAnchor: [19, 19],
        });
    };

    return (
        <>
            {players
                .filter(player => player.dimension === selectedDimension)
                .map(player => {
                    const isHighlighted = highlightedPlayer === player.name;
                    return (
                        <Marker
                            key={`player-${player.uuid || player.name}`}
                            position={blockToLatLng(player.x, player.z, map.getZoom())}
                            icon={getPlayerIcon(player.name, stale, isHighlighted)}
                            eventHandlers={{
                                click: () => {
                                    navigate(`/${player.name}`);
                                }
                            }}
                        >
                            <Tooltip direction="top" offset={[0, -15]} opacity={1}>
                                <div>
                                    <strong>{player.name}</strong>
                                    <br />
                                    X: {Math.round(player.x)}, Y: {Math.round(player.y)}, Z: {Math.round(player.z)}
                                    <br />
                                    Dimension: {player.dimension}
                                    {stale && (
                                        <>
                                            <br />
                                            <span style={{ color: '#ff6b6b', fontSize: '0.9em' }}>
                                                ⚠️ Position may be stale
                                            </span>
                                        </>
                                    )}
                                </div>
                            </Tooltip>
                        </Marker>
                    );
                })}
        </>
    );
};

// Coordinate tooltip that follows mouse cursor
const CoordinateTooltip = () => {
    const map = useMap();
    const [coordinates, setCoordinates] = useState({ x: 0, z: 0 });
    const [showTooltip, setShowTooltip] = useState(false);
    const [isOverMarker, setIsOverMarker] = useState(false);

    useEffect(() => {
        let tooltipElement = null;

        const createTooltip = () => {
            tooltipElement = L.DomUtil.create('div', 'coordinate-tooltip');
            tooltipElement.style.cssText = `
                position: absolute;
                background: rgba(0, 0, 0, 0.8);
                color: white;
                padding: 6px 10px;
                border-radius: 4px;
                font-size: 12px;
                font-family: monospace;
                white-space: nowrap;
                pointer-events: none;
                z-index: 1000;
                box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                border: 1px solid rgba(255,255,255,0.2);
                display: none;
            `;
            map.getContainer().appendChild(tooltipElement);
        };

        const isMarkerElement = (target) => {
            // Check if target or any parent element is a marker
            let element = target;
            while (element && element !== document) {
                if (element.classList && (
                    element.classList.contains('poi-marker') ||
                    element.classList.contains('player-marker') ||
                    element.classList.contains('origin-marker') ||
                    element.classList.contains('leaflet-marker-icon') ||
                    element.classList.contains('leaflet-tooltip')
                )) {
                    return true;
                }
                element = element.parentElement;
            }
            return false;
        };

        const updateTooltip = (e) => {
            if (!tooltipElement) return;

            // Check if we're hovering over a marker
            const overMarker = isMarkerElement(e.originalEvent.target);
            setIsOverMarker(overMarker);

            // Don't show coordinate tooltip if we're over a marker
            if (overMarker) {
                tooltipElement.style.display = 'none';
                return;
            }

            const latlng = e.latlng;
            const zoom = map.getZoom();

            // Convert from Leaflet coordinates to Minecraft world coordinates
            // At zoom 0, 1 leaflet unit = 16 blocks, so multiply by 16
            const worldX = Math.round(latlng.lng * 16);
            const worldZ = Math.round(-latlng.lat * 16);

            setCoordinates({ x: worldX, z: worldZ });

            tooltipElement.innerHTML = `X: ${worldX}, Z: ${worldZ}`;
            tooltipElement.style.left = (e.containerPoint.x + 15) + 'px';
            tooltipElement.style.top = (e.containerPoint.y - 35) + 'px';
            tooltipElement.style.display = 'block';
        };

        const hideTooltip = () => {
            if (tooltipElement) {
                tooltipElement.style.display = 'none';
            }
            setShowTooltip(false);
            setIsOverMarker(false);
        };

        const showTooltipHandler = () => {
            setShowTooltip(true);
        };

        // Create tooltip element
        createTooltip();

        // Add event listeners
        map.on('mousemove', updateTooltip);
        map.on('mouseout', hideTooltip);
        map.on('mouseover', showTooltipHandler);

        // Cleanup
        return () => {
            map.off('mousemove', updateTooltip);
            map.off('mouseout', hideTooltip);
            map.off('mouseover', showTooltipHandler);

            if (tooltipElement && tooltipElement.parentNode) {
                tooltipElement.parentNode.removeChild(tooltipElement);
            }
        };
    }, [map]);

    return null;
};

// Map event handler for POI fetching
const MapEvents = ({ onBoundsChange }) => {
    const debounceTimer = useRef(null);
    const map = useMap();

    // Store map instance globally for POI fetching
    useEffect(() => {
        window.mapInstance = map;
        return () => {
            delete window.mapInstance;
        };
    }, [map]);

    const handleBoundsChange = useCallback(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
            onBoundsChange();
        }, 250);
    }, [onBoundsChange]);

    useMapEvents({
        moveend: handleBoundsChange,
        zoomend: handleBoundsChange,
    });

    return null;
};

const MapPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [minecraftInfo, setMinecraftInfo] = useState(null);
    const [players, setPlayers] = useState([]);
    const [playerStale, setPlayerStale] = useState(false);
    const [pois, setPois] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hoveredPlayer, setHoveredPlayer] = useState(null);
    const mapRef = useRef(null);
    const [selectedDimension, setSelectedDimension] = useState('overworld');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // POI types based on dimension
    const getDimensionPoiTypes = (dimension) => {
        switch (dimension) {
            case 'the_nether':
                return { fortress: true, bastion: true };
            case 'the_end':
                return { endcity: true };
            default:
                return { village: true, monument: true, mansion: true, outpost: true, temple: true };
        }
    };

    const [toggledTypes, setToggledTypes] = useState(getDimensionPoiTypes('overworld'));

    // Parse URL parameters for map center and zoom
    const centerParam = searchParams.get('center');
    const zoomParam = searchParams.get('zoom');

    let initialCenter = [0, 0]; // Default center
    let initialZoom = 0; // Default zoom

    if (centerParam) {
        const [x, z] = centerParam.split(',').map(Number);
        if (!isNaN(x) && !isNaN(z)) {
            initialCenter = blockToLatLng(x, z);
        }
    }

    if (zoomParam) {
        const zoom = parseInt(zoomParam, 10);
        if (!isNaN(zoom)) {
            initialZoom = zoom;
        }
    }

    // Function to center the map on a player's coordinates
    const centerMapOnPlayer = (player) => {
        if (mapRef.current && player.x !== undefined && player.z !== undefined) {
            const map = mapRef.current;
            const playerLatLng = blockToLatLng(player.x, player.z);
            map.setView(playerLatLng, 4); // Zoom level 4 as requested
        }
    };

    // Fetch Minecraft info on mount
    useEffect(() => {
        const fetchMinecraftInfo = async () => {
            try {
                const response = await apiRequest('/minecraft/info');
                const data = await response.json();
                setMinecraftInfo(data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching Minecraft info:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchMinecraftInfo();
    }, []);

    // Poll players every 3 seconds
    useEffect(() => {
        if (!minecraftInfo) return;

        const fetchPlayers = async () => {
            try {
                const response = await apiRequest('/minecraft/players');
                const data = await response.json();
                setPlayers(data.players || []);
                setPlayerStale(data.stale || false);
            } catch (err) {
                console.error('Error fetching players:', err);
            }
        };

        fetchPlayers(); // Initial fetch
        const interval = setInterval(fetchPlayers, 3000);

        return () => clearInterval(interval);
    }, [minecraftInfo]);

    // Fetch POIs when bounds change
    const fetchPoisForCurrentView = useCallback(async () => {
        if (!minecraftInfo) return;

        // Get map instance from the MapEvents component
        const mapInstance = window.mapInstance;
        if (!mapInstance) return;

        try {
            const bounds = mapInstance.getBounds();
            const zoom = mapInstance.getZoom();
            const bbox = mapBoundsToBlockBbox(bounds, zoom);

            const enabledTypes = Object.entries(toggledTypes)
                .filter(([_, enabled]) => enabled)
                .map(([type]) => type);

            if (enabledTypes.length === 0) {
                setPois([]);
                return;
            }

            const bboxString = `${bbox.minX},${bbox.minZ},${bbox.maxX},${bbox.maxZ}`;
            const typesString = enabledTypes.join(',');

            const response = await fetch(
                `/api/map/pois?seed=${minecraftInfo.seed}&ver=${minecraftInfo.version}&dimension=${selectedDimension}&bbox=${bboxString}&types=${typesString}`
            );

            if (!response.ok) throw new Error('Failed to fetch POIs');
            const data = await response.json();
            setPois(data.pois || []);
        } catch (err) {
            console.error('Error fetching POIs:', err);
        }
    }, [minecraftInfo, toggledTypes, selectedDimension]);

    // Fetch POIs when toggles change
    useEffect(() => {
        fetchPoisForCurrentView();
    }, [fetchPoisForCurrentView]);

    // Reset POI types when dimension changes
    useEffect(() => {
        setToggledTypes(getDimensionPoiTypes(selectedDimension));
    }, [selectedDimension]);

    const handleTypeToggle = (type) => {
        setToggledTypes(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    if (loading) {
        return (
            <div className="map-page">
                <div className="loading">Loading map...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="map-page">
                <div className="error">Error: {error}</div>
            </div>
        );
    }

    return (
        <div className="map-page">
            <div className={`map-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="home-button-container">
                    <button
                        className="home-button"
                        onClick={() => navigate('/')}
                        title="Return to Home"
                    >
                        {sidebarCollapsed ? '🏠' : '🏠 Home'}
                    </button>
                </div>

                {!sidebarCollapsed && (
                    <>
                        <div className="dimension-selector">
                            <h4>Dimension</h4>
                            <select
                                value={selectedDimension}
                                onChange={(e) => setSelectedDimension(e.target.value)}
                                className="dimension-dropdown"
                            >
                                <option value="overworld">Overworld</option>
                                <option value="the_nether">The Nether</option>
                                <option value="the_end">The End</option>
                            </select>
                        </div>

                        <div className="map-info">
                            <h3>Server Info</h3>
                            <div className="info-item">
                                <strong>Seed:</strong> {minecraftInfo?.seed}
                            </div>
                            <div className="info-item">
                                <strong>Version:</strong> {minecraftInfo?.version}
                            </div>
                            <div className="info-item">
                                <strong>Players Online:</strong> {players.length}
                                {playerStale && (
                                    <span className="stale-warning">⚠️</span>
                                )}
                            </div>
                            {players.length > 0 && (
                                <div className="info-item">
                                    <div className="player-list">
                                        {players
                                            .map((player, index) => (
                                                <div
                                                    key={player.uuid || player.name || index}
                                                    className="player-item"
                                                    onMouseEnter={() => setHoveredPlayer(player.name)}
                                                    onMouseLeave={() => setHoveredPlayer(null)}
                                                >
                                                    <Link
                                                        to={`/${encodeURIComponent(player.name)}`}
                                                        className="player-name"
                                                    >
                                                        {player.name}
                                                    </Link>
                                                    {player.x !== undefined && player.z !== undefined && (
                                                        <span
                                                            className="player-coords clickable-coords"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                // Switch dimension if different
                                                                if (player.dimension !== selectedDimension) {
                                                                    setSelectedDimension(player.dimension);
                                                                }
                                                                centerMapOnPlayer(player);
                                                            }}
                                                            title="Click to center map on player"
                                                        >
                                                            <i>{player.dimension.replace('the_', '').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</i> ({Math.round(player.x)}, {player.y !== undefined ? Math.round(player.y) : '?'}, {Math.round(player.z)})
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="poi-toggles">
                            <h4>Show POIs</h4>
                            {Object.entries(toggledTypes).map(([type, enabled]) => (
                                <label key={type} className="toggle-item">
                                    <input
                                        type="checkbox"
                                        checked={enabled}
                                        onChange={() => handleTypeToggle(type)}
                                    />
                                    <span className="capitalize">{type}</span>
                                </label>
                            ))}
                        </div>
                    </>
                )}

                <button
                    className="collapse-toggle"
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                >
                    {sidebarCollapsed ? '→' : '←'}
                </button>
            </div>

            <div className="map-container">
                <MapContainer
                    ref={mapRef}
                    crs={MinecraftCRS}
                    center={initialCenter}
                    zoom={initialZoom}
                    style={{ height: '100%', width: '100%' }}
                >
                    {minecraftInfo && (
                        <MinecraftTileLayer
                            seed={minecraftInfo.seed}
                            version={minecraftInfo.version}
                            dimension={selectedDimension}
                        />
                    )}
                    <CoordinateTooltip />
                    <MapEvents onBoundsChange={fetchPoisForCurrentView} />
                    <OriginMarker />
                    <PoiMarkers pois={pois} toggledTypes={toggledTypes} />
                    <PlayerMarkers players={players} stale={playerStale} highlightedPlayer={hoveredPlayer} selectedDimension={selectedDimension} />
                </MapContainer>
            </div>
        </div>
    );
};

export default MapPage;